'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';

interface PlaceholdersAndVanishInputProps {
  placeholder: string;
  bgImg: string;
  /** 提交按钮文案；不传时用默认「去创作」。 */
  buttonText?: string;
  /** 提示词前的标签文案（如「Vidu S1 实时交互模型」）；不传则不展示标签。 */
  tagText?: string;
  /** 是否隐藏左侧缩略图（S1 展示视频不显示图片）。 */
  hideImage?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (text?: string) => void;
}

export const PlaceholdersAndVanishInput = ({
  placeholder,
  bgImg,
  buttonText,
  tagText,
  hideImage = false,
  onChange,
  onSubmit,
}: PlaceholdersAndVanishInputProps) => {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const locale = useLocale();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // 提交当前值或占位符
      const currentValue = inputRef.current?.value || '';
      const finalValue = currentValue ? currentValue : placeholder;
      onSubmit?.(finalValue);
      setValue('');
    },
    [placeholder, onSubmit],
  );

  // 处理输入变化的函数
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  return (
    <form
      className={cn(
        'relative mx-auto flex h-full w-full items-center justify-center overflow-hidden rounded-full border-system-white36 bg-system-white24 shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),_0px_1px_0px_0px_rgba(25,28,33,0.02),_0px_0px_0px_1px_rgba(25,28,33,0.08)] backdrop-blur-[20px] transition duration-200',
      )}
    >
      {hideImage ? null : (
        <div className="ml-6 flex items-center">
          <Image
            className="size-7 rounded-8 object-cover object-center"
            src={bgImg}
            alt="banner-img"
            width={28}
            height={28}
            loading="lazy"
            decoding="async"
          />
          <span className="mx-4 inline-block h-6 w-2 text-center text-base text-white">|</span>
        </div>
      )}

      <input
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        value={value}
        type="text"
        className={cn(
          'relative z-50 h-full flex-1 rounded-full border-none bg-transparent pr-44 font-normal text-base focus:outline-none focus:ring-0',
          hideImage ? 'pl-6' : 'pl-1',
        )}
      />

      <button
        type="button"
        className="-translate-y-1/2 peer absolute top-1/2 right-2 z-50 flex h-12 w-[142px] cursor-pointer items-center rounded-full bg-black text-white transition duration-200 disabled:bg-[rgba(0,0,0,.8)]"
        onClick={handleSubmit}
      >
        <div className="relative mx-4 inline-flex w-full items-center justify-center">
          <div className={cn('absolute font-medium text-base', locale === 'zh' && 'ps-2 pe-2')}>
            {buttonText ?? t('home3.heroSection.createButton')}
          </div>
        </div>
      </button>

      <div className="pointer-events-none absolute inset-0 flex items-center rounded-full">
        <AnimatePresence mode="wait">
          {!value && (
            <motion.p
              initial={{
                y: 5,
                opacity: 0,
              }}
              key={`current-placeholder-${placeholder}`}
              animate={{
                y: 0,
                opacity: 1,
              }}
              exit={{
                y: -15,
                opacity: 0,
              }}
              transition={{
                duration: 0.3,
                ease: 'linear',
              }}
              className={cn(
                'flex w-[calc(100%-10rem)] items-center gap-2 truncate text-left font-normal text-base text-white',
                hideImage ? 'pl-6' : 'pl-24 sm:pl-12',
              )}
            >
              {tagText ? (
                <span className="shrink-0 whitespace-nowrap rounded-10 bg-white/12 px-3 py-1 font-medium text-[13px] text-white backdrop-blur-[10px]">
                  {tagText}
                </span>
              ) : null}
              <span className="truncate">{placeholder}</span>
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
};
